import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Divider, FormControl, MenuItem, Select, Typography } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setSelectedSchool } from '../../../actions/user.actions';
import { selectSelectedSchoolId, selectUser } from '../../../reducers/user.selectors';

const SchoolSelector = () => {
	const dispatch = useDispatch();
	const user = useSelector(selectUser);
	const selectedSchoolId = useSelector(selectSelectedSchoolId);
	const hasSetDefaultSchool = useRef(false);

	// Set default school when component mounts
	useEffect(() => {
		if (user && user.manageableSchools && user.manageableSchools.length > 0) {
			// Always set the first school as default if no school is selected
			const defaultSchool = user.manageableSchools[0];

			if (defaultSchool && !selectedSchoolId && !hasSetDefaultSchool.current) {
				hasSetDefaultSchool.current = true;
				dispatch(setSelectedSchool(defaultSchool.id));
			}
		}
	}, [user, user?.manageableSchools, selectedSchoolId, dispatch]);

	const handleSchoolChange = (event: any) => {
		const newSchoolId = event.target.value;
		dispatch(setSelectedSchool(newSchoolId));
	};

	// Don't render if no manageable schools
	if (!user?.manageableSchools || user.manageableSchools.length === 0) {
		return <></>;
	}

	const currentSchoolId = selectedSchoolId || user.manageableSchools[0]?.id;
	return (
		<>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				sx={{
					width: '100%',
					backgroundColor: 'rgb(249, 250, 251)',
					borderRadius: '8px',
					padding: '16px 0',
				}}
			>
				<Box className="container" sx={{ width: '100%' }}>
					<Box display="flex" alignItems="center" gap={3}>
						<FormControl sx={{ minWidth: 200, maxWidth: 400 }}>
							<Select
								id="school-select"
								value={currentSchoolId || ''}
								onChange={handleSchoolChange}
								displayEmpty
								IconComponent={KeyboardArrowDownIcon}
								sx={{
									backgroundColor: 'white',
									borderRadius: '8px',
									fontSize: '12px',
									'& .MuiOutlinedInput-notchedOutline': {
										border: '1px solid #e0e0e0',
									},
									'&:hover .MuiOutlinedInput-notchedOutline': {
										border: '1px solid #c0c0c0',
									},
									'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
										border: '2px solid #1976d2',
									},
									'& .MuiSelect-select': {
										padding: '10px 14px',
										display: 'flex',
										alignItems: 'center',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									},
									'& .MuiSelect-icon': {
										right: '8px',
										fontSize: '20px',
										color: '#666',
									},
									boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
									'&:hover': {
										boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
									},
								}}
							>
								{user?.manageableSchools?.map((school) => (
									<MenuItem
										key={school.id}
										value={school.id}
										sx={{
											backgroundColor:
												school.id === currentSchoolId ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
											'&:hover': {
												backgroundColor:
													school.id === currentSchoolId ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
											},
											'&.Mui-selected': {
												backgroundColor: 'rgba(25, 118, 210, 0.08)',
												'&:hover': {
													backgroundColor: 'rgba(25, 118, 210, 0.12)',
												},
											},
										}}
									>
										<Box
											sx={{
												width: '100%',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
											}}
										>
											<Typography
												variant="body1"
												fontWeight={school.id === currentSchoolId ? 600 : 500}
												sx={{
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
													maxWidth: '100%',
													color: school.id === currentSchoolId ? '#1976d2' : 'inherit',
												}}
											>
												{school.name} ({school.id})
											</Typography>
										</Box>
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>
				</Box>
			</Box>
			<Divider
				sx={{
					borderColor: 'rgba(0, 0, 0, 0.12)',
				}}
			/>
		</>
	);
};

export default SchoolSelector;
